import { useCallback, useState } from "react";

import { Button } from "#/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#/components/ui/dialog";

function UndoTurnConfirmDialog({
  open,
  restoreDay,
  onDismiss,
  onConfirm,
  pending,
}: {
  open: boolean;
  restoreDay: number;
  onDismiss: () => void;
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <DialogContent showCloseButton={false} initialFocus={false}>
        <DialogHeader>
          <DialogTitle>Undo Turn?</DialogTitle>
          <DialogDescription>
            This will put you back at the end of turn {restoreDay}.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDismiss}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={() => void onConfirm()}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useUndoTurnConfirmation({
  restoreDay,
  onConfirm,
  pending,
}: {
  restoreDay: number;
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const requestUndoTurn = useCallback(() => {
    setOpen(true);
  }, []);

  const dismiss = useCallback(() => {
    setOpen(false);
  }, []);

  const confirmAndClose = useCallback(async () => {
    setOpen(false);
    await onConfirm();
  }, [onConfirm]);

  const confirmDialog = (
    <UndoTurnConfirmDialog
      open={open}
      restoreDay={restoreDay}
      onDismiss={dismiss}
      onConfirm={confirmAndClose}
      pending={pending}
    />
  );

  return { requestUndoTurn, confirmDialog, dialogOpen: open };
}
